import { useState } from 'react'

import { Icon, Tooltip, useNotifications } from '@joor/design-system'
import classNames from 'classnames'
import { List } from 'semantic-ui-react'
import styled from 'styled-components/macro'

import { FORMAT_EXCEL, FORMAT_PDF, exportOrders } from 'utils/requests'

import { useRenderToasters } from 'hooks/useRenderToasters'

import { Checkbox, Modal, Radio } from 'components/Core'

import { DownloadOrderIds } from './DownloadOrderModal.ids'
import messages from './DownloadOrderModal.messages'
import styles from './DownloadOrderModal.module.css'
import { useDownloadRawData } from './DownloadOrderModal.mutations'

const ORDER_RAW_DATA = 'ORDER_RAW_DATA'

type ExportOption = {
  label: string
  value: string
}
type DownloadOrderModalType = {
  onClose: () => void
  selectedOrdersIds: string[]
  handleExportAction: (
    action: () => void,
    successMessage: string,
    failMessage: string,
    taskId?: string,
  ) => void
  trackDownloadOrderModalConfirmationSubmitted: (param: unknown) => void
  trackDownloadOrderModalConfirmationCancelled: () => void
}

const StyledIcon = styled(Icon)`
  margin-left: 8px;
`

const DownloadOrderModal = ({
  onClose,
  selectedOrdersIds,
  handleExportAction,
  trackDownloadOrderModalConfirmationCancelled,
  trackDownloadOrderModalConfirmationSubmitted,
}: DownloadOrderModalType) => {
  const [format, setFormat] = useState(
    selectedOrdersIds.length ? FORMAT_PDF : ORDER_RAW_DATA,
  )
  const [options, setOptions] = useState<{ [key: string]: string }>({})
  const downloadRawData = useDownloadRawData()
  const { renderErrorToast, renderWarningToast } = useRenderToasters()
  const { activeToasts, removeToasts } = useNotifications()
  const pendingToast = activeToasts.find(
    (toast) => toast.title === 'Download Pending',
  )

  const onSave = () => {
    if (format === ORDER_RAW_DATA) {
      renderWarningToast({
        title: 'Download Pending',
        description:
          'Your download is being prepared and will start as soon as it is ready.',
        shouldCloseManually: true,
      })
      downloadRawData().then(({ data, errors }) => {
        if (errors) {
          if (pendingToast) {
            removeToasts([pendingToast])
          }
          renderErrorToast({
            title: 'Download failed',
            description: 'An error occured with your download',
          })
          return
        }

        handleExportAction(
          () => {},
          messages.downloadCompleted,
          messages.failedExport,
          data?.createRawOrdersExport.taskId,
        )
      })

      return
    }

    const formatSelected = format === FORMAT_PDF ? 'PDF' : 'Excel'

    trackDownloadOrderModalConfirmationSubmitted({ formatSelected })

    handleExportAction(
      () => exportOrders(selectedOrdersIds, format, options),
      messages.downloadCompleted,
      messages.failedExport,
    )
  }

  const updateFormat = (_: unknown, { value }: { value: string }) => {
    setFormat(value)
    setOptions({})
  }

  const toggleOption = (e: unknown, { value }: { value: string }) => {
    const optionIsEnabled = options[value] && options[value] === 'true'

    setOptions({
      ...options,
      [value]: optionIsEnabled ? 'false' : 'true',
    })
  }

  const handleClose = () => {
    trackDownloadOrderModalConfirmationCancelled()
    onClose()
  }

  const renderPDFOptions = () => {
    const exportOptions: ExportOption[] = [
      { label: messages.mergeOrders, value: 'merge_all_pdf' },
      { label: messages.excludeTerms, value: 'exclude_terms' },
      { label: messages.excludeImages, value: 'no_images_export' },
      { label: messages.linesheetFormat, value: 'linesheet_format' },
    ]
    return (
      <List>
        {exportOptions.map((exportOption) => renderExportOption(exportOption))}
      </List>
    )
  }

  const renderExcelOptions = () => {
    const exportOptions = [
      { label: messages.mergeOrders, value: 'merge_all_excel_po' },
      { label: messages.excludeTerms, value: 'exclude_terms_excel_po' },
      { label: messages.excludeImages, value: 'no_images_export_excel_po' },
    ]
    return (
      <List>
        {exportOptions.map((exportOption) => renderExportOption(exportOption))}
      </List>
    )
  }

  const renderExportOption = (exportOption: ExportOption) => {
    const { value, label } = exportOption
    const isSelected = options[value] && options[value] === 'true'

    return (
      <List.Item key={value}>
        <Checkbox
          label={label}
          value={value}
          checked={isSelected}
          onClick={toggleOption}
        />
      </List.Item>
    )
  }

  return (
    <Modal
      title={messages.downloadOrderConfirmation}
      primaryActionOnClick={onSave}
      primaryActionLabel={messages.download}
      onClose={handleClose}
      open
      primaryActionId={DownloadOrderIds.DownloadOrderButton}
    >
      <span
        className={classNames(
          styles.label,
          !selectedOrdersIds.length ? styles.disabled : '',
        )}
      >
        {messages.downloadFormat}:
      </span>
      <List>
        <List.Item>
          <Radio
            id={DownloadOrderIds.DownloadOrderPDF}
            checked={format === FORMAT_PDF}
            label={messages.pdf}
            value={FORMAT_PDF}
            onClick={updateFormat}
            disabled={!Boolean(selectedOrdersIds.length)}
          />
          {format === FORMAT_PDF && renderPDFOptions()}
        </List.Item>
        <List.Item>
          <Radio
            id={DownloadOrderIds.DownloadOrderExcel}
            checked={format === FORMAT_EXCEL}
            label={messages.excel}
            value={FORMAT_EXCEL}
            onClick={updateFormat}
            disabled={!Boolean(selectedOrdersIds.length)}
          />
          {format === FORMAT_EXCEL && renderExcelOptions()}
        </List.Item>
      </List>
      <>
        <span
          className={classNames(
            styles.label,
            selectedOrdersIds.length ? styles.disabled : '',
          )}
        >
          {messages.downloadAll}:
        </span>
        <List>
          <List.Item className={styles.styledListItem}>
            <Radio
              id={DownloadOrderIds.DownloadOrderRaw}
              checked={format === ORDER_RAW_DATA}
              label={messages.rawOrderData}
              value={ORDER_RAW_DATA}
              onClick={updateFormat}
              disabled={Boolean(selectedOrdersIds.length)}
            />
            <Tooltip
              content={
                selectedOrdersIds.length
                  ? 'This option is not available when one or more orders have been selected.'
                  : 'This option will export the data for all orders.'
              }
              defaultPlacement="right"
            >
              <StyledIcon
                iconName="exclamation"
                variant={'ACTIVE'}
                iconSize="small"
              />
            </Tooltip>
          </List.Item>
        </List>
      </>
    </Modal>
  )
}

export default DownloadOrderModal
